import type { EtlJob, EtlJobCatalog } from '../../types/etlJobs'
import type { RequestedRun } from '../../types/etlProgress'
import type {
  ExecutionAnomalies,
  ExecutionCountsBasis,
  ExecutionEntity,
  ExecutionListItem,
  ExecutionOutcome,
  ExecutionPage,
  ExecutionQuery,
} from '../../types/executions'
import { NOT_MEASURED, duration, errorKind, exactNumber, relativeTime } from '../../utils/format'
import type { ErrorKind } from '../../utils/format'
import { HALT_CLEARED_MARKER, haltDetailView } from '../../utils/haltDetail'
import { entityOutcomeLook, runOutcomeLook } from '../outcomeFormat'
import { deriveRunGate } from '../../utils/adminGate'
import type { OutcomeLook } from '../outcomeFormat'

/**
 * Execution History's pure logic — everything the list and the detail decide
 * that is not layout, so it can be tested without rendering.
 *
 * Nothing here recomputes an outcome, a count or a staleness verdict: the
 * server serves those (ObservatoryExecutionsController, through the one
 * ExecutionOutcomes mapping). This module only says how to SHOW them, and a
 * null the server sends becomes NOT_MEASURED, never 0 or a green state.
 */

/** Runs per page. Server-side paging; the endpoint accepts 1..100 and refuses anything else. */
export const PAGE_SIZE = 25

/**
 * The seven run outcomes, in the status filter's order. Also the only values
 * the endpoint's `status` accepts — anything else is a 400, so the list never
 * sends one it did not find here.
 */
export const RUN_OUTCOMES: readonly ExecutionOutcome[] = [
  'running',
  'stale',
  'succeeded',
  'succeededWithWarnings',
  'failed',
  'halted',
  'cancelled',
]

/**
 * How an outcome is shown. A null role is a plain gray badge: neither good news nor bad.
 *
 * The labels and roles are components/outcomeFormat.ts's — the one map every surface that shows
 * this vocabulary reads, because an entity row here links into the Explorer and must read the same
 * there.
 */
export type OutcomePresentation = OutcomeLook

/**
 * A run outcome's label and badge role. An outcome this build does not know
 * (the server serves a new EtlRunStatus member by its own name) is shown by
 * that name on a plain badge — never mapped to a confident guess.
 */
export function runOutcomePresentation(outcome: string): OutcomePresentation {
  return runOutcomeLook(outcome)
}

/** An entity row's outcome label and badge role; unknown ones as {@link runOutcomePresentation}. */
export function entityOutcomePresentation(outcome: string): OutcomePresentation {
  return entityOutcomeLook(outcome)
}

/**
 * What the run was created to load: '{entity}', 'Module {module}' or 'Whole
 * warehouse'. A dash when nothing parseable was recorded — never a guess at the
 * whole warehouse.
 */
export function targetText(requested: RequestedRun | null): string {
  if (!requested) return NOT_MEASURED
  if (requested.entity) return requested.entity
  if (requested.isWholeWarehouse) return 'Whole warehouse'
  if (requested.module) return `Module ${requested.module}`
  return NOT_MEASURED
}

/**
 * The window the run was created for, as the plain dates it was recorded as
 * (never converted to instants: the source column carries no timezone). The
 * end is exclusive, which the column header says.
 */
export function windowText(requested: RequestedRun | null): string {
  if (!requested || !requested.start || !requested.end) return NOT_MEASURED
  return `${requested.start} – ${requested.end}`
}

/** The Window column's header tooltip. */
export const WINDOW_HEADER_HINT =
  'As recorded when the run was created. Start inclusive, end exclusive — a resume reuses the run under a window that is not recorded.'

/** Said beside a run's counts when they may be short with nothing served saying so ({@link countsMayBeShort}). */
export const COUNTS_MAY_BE_SHORT = 'counted from surviving rows — may be short'

/** The longer form, for the Records figure's tooltip and the detail's Records card. */
export const COUNTS_MAY_BE_SHORT_DETAIL =
  'This run has no rollup (it halted, or its worker died holding it), so its counts and records come from its '
  + 'entity rows, and finished rows past retention are gone. Whether any are missing cannot be told, so these '
  + 'figures are a floor, not a count.'

/**
 * Whether a run's counts and records loaded may be short with nothing on the wire saying so.
 *
 * A run with no rollup is counted from its rows (`countsBasis: 'entityRows'`), and then whether rows are
 * missing is NOT KNOWABLE (`entityRowsTrimmed: null`). A live run's finished rows are younger than the
 * retention window, but a halted run, or a stale one (a dead worker's leftover that nothing closes), can
 * be old enough for retention to have trimmed its finished rows, and the server's records caveat is sent
 * only when trimming is KNOWN (`entityRowsTrimmed: true`). So those two say so here.
 */
export function countsMayBeShort(
  item: Pick<ExecutionListItem, 'countsBasis' | 'entityRowsTrimmed' | 'outcome'>,
): boolean {
  return item.countsBasis === 'entityRows'
    && item.entityRowsTrimmed == null
    && (item.outcome === 'halted' || item.outcome === 'stale')
}

/** The caveat shown beside records loaded: the server's when it sent one, else ours when the counts may be short. */
export function recordsLoadedCaveatFor(
  item: Pick<ExecutionListItem, 'countsBasis' | 'entityRowsTrimmed' | 'outcome' | 'recordsLoadedCaveat'>,
): string | null {
  return item.recordsLoadedCaveat ?? (countsMayBeShort(item) ? COUNTS_MAY_BE_SHORT_DETAIL : null)
}

/**
 * '{succeeded} ok / {failed} failed / {skipped} skipped of {total}', then any
 * rows still moving: running, stale (a Running row whose heartbeat is older
 * than the rule — the other half of "running", so leaving it out would hide
 * it) and not started — then, when {@link countsMayBeShort}, that they may be.
 *
 * `of {total}` is TotalEntities, fixed when the run was created: a resume can
 * put more rows in the run than that, which the detail's anomalies say.
 */
export function entityCountSummary(item: ExecutionListItem): string {
  const parts = [
    `${exactNumber(item.entitiesSucceeded)} ok / ${exactNumber(item.entitiesFailed)} failed / `
      + `${exactNumber(item.entitiesSkipped)} skipped of ${exactNumber(item.totalEntitiesRecordedAtCreation)}`,
  ]
  if (item.entitiesRunning > 0) parts.push(`${exactNumber(item.entitiesRunning)} running`)
  if (item.entitiesStale > 0) parts.push(`${exactNumber(item.entitiesStale)} stale`)
  if (item.entitiesPending > 0) parts.push(`${exactNumber(item.entitiesPending)} not started`)
  if ((item.entitiesCancelled ?? 0) > 0) parts.push(`${exactNumber(item.entitiesCancelled ?? 0)} cancelled`)
  if (countsMayBeShort(item)) parts.push(COUNTS_MAY_BE_SHORT)
  return parts.join(' · ')
}

/**
 * 'last heard 3h ago', from the newest heartbeat. A stale row that never beat
 * says so rather than printing a dash that reads like "nothing to report".
 */
export function lastHeardText(lastHeartbeatAt: string | null): string {
  return lastHeartbeatAt ? `last heard ${relativeTime(lastHeartbeatAt)}` : 'no heartbeat recorded'
}

/**
 * A run's duration as served. A stale run has none — when it stopped is not
 * recorded, and "to now" would grow for ever — so it says when it was last
 * heard from instead. A dash when the server recorded no duration.
 */
export function durationText(item: Pick<ExecutionListItem, 'outcome' | 'durationSeconds' | 'lastHeartbeatAt'>): string {
  if (item.outcome === 'stale') return lastHeardText(item.lastHeartbeatAt)
  return item.durationSeconds == null ? NOT_MEASURED : duration(item.durationSeconds)
}

/**
 * An entity row's duration. The row carries only its instants, so this is
 * measured here: completed minus started, or to now while it is live. Stale
 * says when it was last heard from; anything without a start is a dash.
 */
export function entityDurationText(entity: ExecutionEntity, now: number = Date.now()): string {
  if (entity.outcome === 'stale') return lastHeardText(entity.lastHeartbeatAt)
  const started = entity.startedAt ? new Date(entity.startedAt).getTime() : Number.NaN
  if (Number.isNaN(started)) return NOT_MEASURED
  if (entity.completedAt) {
    const completed = new Date(entity.completedAt).getTime()
    return Number.isNaN(completed) ? NOT_MEASURED : duration((completed - started) / 1000)
  }
  return entity.outcome === 'running' ? duration((now - started) / 1000) : NOT_MEASURED
}

/**
 * Where the numbers come from, in words. A finished run is counted by the
 * rollup it wrote when it finished (which outlives its trimmed entity rows); a
 * running or halted one by its rows.
 */
export function countsBasisText(basis: ExecutionCountsBasis | string): string {
  if (basis === 'runHeader') return "The run's own rollup, written when it finished"
  if (basis === 'entityRows') return 'Its entity rows — it has no rollup (still running, or halted)'
  return basis
}

/**
 * Whether the entity rows the detail lists are all of them. Null is NOT
 * KNOWABLE (no rollup to compare against), which is a dash plus the reason —
 * never "complete".
 */
export function entityRowsTrimmedText(trimmed: boolean | null): string {
  if (trimmed === true) return "Some finished rows are past retention — the list below is short; the counts above are not"
  if (trimmed === false) return 'Every row the rollup accounts for is on file'
  return `${NOT_MEASURED} not knowable: the run has no rollup to compare its rows against`
}

/** The stale rule in words, saying when it is the code default rather than what Automaton published. */
export function staleRuleText(rule: { minutes: number; published: boolean }): string {
  const base = `Stale means Running with no heartbeat for ${exactNumber(rule.minutes)} minutes`
  return rule.published ? base : `${base} (the code default — Automaton has not published its value)`
}

/** How a halt's clear note is appended to the recorded detail (EtlHaltController) — utils/haltDetail's marker. */
export const HALT_CLEAR_MARKER = HALT_CLEARED_MARKER

/**
 * The recorded halt detail, split from the note an administrator appended when
 * clearing it — utils/haltDetail's split. The first marker is the one the clear
 * wrote: only the first acknowledge appends, and the note itself may contain
 * anything. A detail the server may have WITHHELD is rendered through
 * `haltDetailView` instead, which tells withheld from none.
 */
export function splitHaltDetail(detail: string | null): { recorded: string | null; clearNote: string | null } {
  const view = haltDetailView(detail, false)
  return view.kind === 'recorded'
    ? { recorded: view.diagnosis, clearNote: view.note }
    : { recorded: null, clearNote: null }
}

/**
 * The uncleared halt card's title. What the halt blocks is said under it by
 * requestFormat.haltConsequenceText, from the categories the server exempts —
 * never a fixed list here that the server could outgrow.
 */
export const HALT_NOT_CLEARED = 'Not cleared'

/**
 * 'Cleared 2h ago by {who}'. The identity is shown as recorded — an opaque id,
 * a name or 'ApiKey' — and a missing one is a dash, never a made-up actor.
 */
export function haltClearedText(acknowledgedAt: string | null, acknowledgedBy: string | null): string {
  const when = acknowledgedAt ? relativeTime(acknowledgedAt) : NOT_MEASURED
  return `Cleared ${when} by ${acknowledgedBy?.trim() || NOT_MEASURED}`
}

/**
 * Whether the run's record holds anything that does not describe the run as
 * created. An unknown resumed count (null, rows trimmed) is not by itself an
 * anomaly — it is shown as a dash wherever the card does appear.
 */
export function hasAnomaly(anomalies: ExecutionAnomalies): boolean {
  return anomalies.entitiesOutsideRecordedTarget.length > 0
    || anomalies.entityRowsExceedTotal
    || (anomalies.resumedEntityCount ?? 0) > 0
}

/**
 * The Scheduler deep link that re-asks for what this run was created for, or
 * null when no catalogue job matches.
 *
 * An entity target opens the Entity job whose displayName is that entity
 * (case-insensitive); a module or whole-warehouse target opens the Warehouse
 * job, with the module for a module load. Parameters the run did not record
 * are omitted rather than sent blank — the Scheduler fills them from the
 * job's own defaults, marks what came from the link, and never auto-submits.
 */
export function schedulerLink(requested: RequestedRun | null, jobs: readonly EtlJob[] | undefined): string | null {
  const job = schedulerJob(requested, jobs)
  if (!job || !requested) return null
  const params = new URLSearchParams({ job: job.jobId })
  if (requested.start) params.set('startDate', requested.start)
  if (requested.end) params.set('endDate', requested.end)
  if (!requested.entity && !requested.isWholeWarehouse && requested.module) params.set('module', requested.module)
  return `/jobs?${params.toString()}`
}

/**
 * Why the Scheduler link is refused to THIS caller, or null — the server's answer (the catalogue's
 * `canTriggerJobs`, the same one the Scheduler's Run and the Explorer's Rerun read — deriveRunGate, not
 * the admin READ answer: the shared API key in Development is an administrator for the read pages and
 * still cannot run anything), never a token claim.
 * Only a served denial refuses it: a catalogue that has not answered, failed, or predates the field
 * leaves the link open, because it only navigates and the Scheduler gates the real request itself.
 */
export function schedulerLinkRefusal(catalog: EtlJobCatalog | undefined): string | null {
  const gate = deriveRunGate(catalog, false)
  return gate.state === 'denied' ? gate.reason || 'The server says this session may not run jobs.' : null
}

/** Why there is no Scheduler link, for the place the button would be. */
export function schedulerLinkUnavailable(
  requested: RequestedRun | null,
  jobs: readonly EtlJob[] | undefined,
): string | null {
  if (!requested || targetText(requested) === NOT_MEASURED) return 'The run recorded no target to ask for again'
  if (!jobs) return 'The job catalogue has not loaded'
  if (!schedulerJob(requested, jobs)) {
    return requested.entity
      ? `No entity job named ${requested.entity} is in the catalogue`
      : 'No Warehouse job is in the catalogue'
  }
  return null
}

function schedulerJob(requested: RequestedRun | null, jobs: readonly EtlJob[] | undefined): EtlJob | undefined {
  if (!requested || !jobs) return undefined
  if (requested.entity) {
    const wanted = requested.entity.toLowerCase()
    return jobs.find((job) => job.category === 'Entity' && job.displayName.toLowerCase() === wanted)
  }
  if (requested.isWholeWarehouse || requested.module) {
    return jobs.find((job) => job.category === 'Warehouse')
  }
  return undefined
}

/**
 * The module filter's options: every module an Entity job in the catalogue
 * belongs to, plus the one in the URL if the catalogue does not list it (a
 * deep link must still show what it filters on).
 */
export function moduleOptions(jobs: readonly EtlJob[] | undefined, current: string | null): string[] {
  const modules = new Set<string>()
  for (const job of jobs ?? []) {
    if (job.category === 'Entity' && job.module?.trim()) modules.add(job.module.trim())
  }
  if (current) modules.add(current)
  return [...modules].sort((a, b) => a.localeCompare(b))
}

/** The list's filters as the URL holds them — `/execution-history?status=stale` is a link somebody can send. */
export interface ListFilters {
  status: ExecutionOutcome | null
  module: string | null
  entity: string | null
  /** yyyy-MM-dd, the day picked; inclusive. */
  from: string | null
  /** yyyy-MM-dd, the day picked; inclusive here, sent as the next day's start (the endpoint's bound is exclusive). */
  to: string | null
  /** 1-based. */
  page: number
  /** A `status` in the URL that is not a run outcome: ignored, and said so, rather than sent for a 400. */
  unknownStatus: string | null
}

/** Reads the filters from the query string. Anything malformed is dropped rather than sent. */
export function readListFilters(params: URLSearchParams): ListFilters {
  const rawStatus = params.get('status')?.trim() || null
  const status = rawStatus
    ? RUN_OUTCOMES.find((outcome) => outcome.toLowerCase() === rawStatus.toLowerCase()) ?? null
    : null
  const page = Number(params.get('page'))
  return {
    status,
    module: params.get('module')?.trim() || null,
    entity: params.get('entity')?.trim() || null,
    from: plainDate(params.get('from')),
    to: plainDate(params.get('to')),
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    unknownStatus: rawStatus && !status ? rawStatus : null,
  }
}

/** Whether anything is narrowing the list — the difference between "none recorded" and "none match". */
export function isFiltered(filters: ListFilters): boolean {
  return filters.status !== null
    || filters.module !== null
    || filters.entity !== null
    || filters.from !== null
    || filters.to !== null
}

/**
 * The URL's filters as the endpoint's query.
 *
 * The dates are the reader's local days: `from` is the start of its day and
 * `to` the start of the day AFTER, because the endpoint's upper bound is
 * exclusive — sending the picked day's own midnight would drop that whole day.
 */
export function toExecutionQuery(filters: ListFilters, pageSize: number = PAGE_SIZE): ExecutionQuery {
  return {
    skip: (filters.page - 1) * pageSize,
    top: pageSize,
    status: filters.status ?? undefined,
    module: filters.module ?? undefined,
    entityName: filters.entity ?? undefined,
    from: filters.from ? localDayStart(filters.from, 0) : undefined,
    to: filters.to ? localDayStart(filters.to, 1) : undefined,
  }
}

/**
 * The query string with one filter set (or removed, for null/empty). Changing
 * any filter returns to the first page: page 4 of the old answer is not a page
 * of the new one.
 */
export function withFilter(current: URLSearchParams, key: keyof Omit<ListFilters, 'unknownStatus'>, value: string | null): URLSearchParams {
  const next = new URLSearchParams(current)
  const trimmed = value?.trim() ?? ''
  if (trimmed === '' || (key === 'page' && trimmed === '1')) next.delete(key)
  else next.set(key, trimmed)
  if (key !== 'page') next.delete('page')
  return next
}

/** A `yyyy-MM-dd` that names a real day, or null. */
function plainDate(value: string | null): string | null {
  const text = value?.trim() ?? ''
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const parsed = new Date(year, month - 1, day)
  return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day ? text : null
}

/** Local midnight of `day` plus `addDays`, as the absolute instant the endpoint filters on. */
function localDayStart(day: string, addDays: number): string {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(year, month - 1, date + addDays).toISOString()
}

/** What the list shows. `|| !data` is load-bearing: between retries a failed query reads as loaded-and-empty. */
export type ListView = 'error' | 'loading' | 'empty' | 'past-end' | 'rows'

export function listView(state: {
  isError: boolean
  isLoading: boolean
  /** The previous filter's page, kept on screen while this one loads. */
  isPlaceholderData?: boolean
  data: ExecutionPage | undefined
}): ListView {
  if (state.isError) return 'error'
  if (state.isLoading || !state.data) return 'loading'
  if (state.data.items.length > 0) return 'rows'
  // The PREVIOUS filter's empty answer is not this filter's: "no runs match" would be said of a question
  // not yet answered. Rows stay on screen (dimmed); an empty placeholder is still loading.
  if (state.isPlaceholderData) return 'loading'
  // Nothing on this page but runs on file: a page number past the end, not an empty history.
  return state.data.totalCount > 0 ? 'past-end' : 'empty'
}

/** '1 run matches these filters', '25 runs on file' — the served total, as a count. */
export function runCountText(totalCount: number, filtered: boolean): string {
  const runs = `${exactNumber(totalCount)} ${totalCount === 1 ? 'run' : 'runs'}`
  if (!filtered) return `${runs} on file`
  return `${runs} ${totalCount === 1 ? 'matches' : 'match'} these filters`
}

/** The empty list's copy. Unfiltered and filtered are different findings. */
export function emptyListCopy(filtered: boolean): { title: string; message: string } {
  return filtered
    ? {
        title: 'No runs match these filters',
        message: 'Nothing in the 365 days of run history matches. Widen the dates or clear the filters.',
      }
    : {
        title: 'No runs recorded',
        message: 'Every ETL job is manual-trigger, so this fills only when someone starts a load from the '
          + 'Scheduler. An empty history means no run has been started, not that runs are not being recorded.',
      }
}

/** What the detail shows. */
export type DetailView = 'not-found' | 'error' | 'loading' | 'ready'

export function detailView(state: { isError: boolean; error: unknown; isLoading: boolean; data: unknown }): DetailView {
  if (state.isError) return errorKind(state.error) === 'not-found' ? 'not-found' : 'error'
  return state.isLoading || !state.data ? 'loading' : 'ready'
}

/** The detail's 404, in the endpoint's own words. */
export function notFoundText(runId: string): string {
  return `No run ${runId}. It may have passed its 365-day retention.`
}

/**
 * A failed request's heading and explanation, by kind. None of them says
 * anything about the runs themselves — a request that did not come back is a
 * fault between this page and the API.
 */
export function errorCopy(kind: ErrorKind): { title: string; message: string } {
  const noVerdict = 'That says nothing about whether any run succeeded.'
  switch (kind) {
    case 'unauthorized':
      return { title: 'Sign-in was not accepted', message: `The API did not accept this session, so no run history was read. ${noVerdict}` }
    case 'forbidden':
      return {
        title: 'The API refused this request',
        message: 'Execution History is served to any signed-in user, so this is not a missing Lighthouse.Admin role; '
          + 'something in front of the API refused it.',
      }
    case 'network':
      return { title: 'Could not reach the API', message: `The request never got an answer. ${noVerdict}` }
    case 'server':
      return { title: 'The API failed while reading run history', message: `It answered with a server error. ${noVerdict}` }
    case 'bad-request':
      return { title: 'The API refused these filters', message: 'Clear the filters and try again.' }
    case 'not-found':
      return { title: 'Run history is not served here', message: 'This API does not have the Execution History endpoint — it may predate it.' }
    default:
      return { title: 'Could not load Execution History', message: noVerdict }
  }
}
